import { ChangeEvent, FormEvent, FunctionComponent, useState } from 'react';

import UserAgree, { typesOptions } from 'models/UserAgree';

import { getErrorsFromRequest } from 'utils/api';

import { create as registerRequest } from 'requests/registerCompany';

import Button from 'components/layout/Button';
import { ButtonTypes } from 'components/layout/Button/types';
import ButtonGoogle from 'components/layout/ButtonGoogle';
import Checkbox, { InputStyles } from 'components/layout/forms/Checkbox';
import Form from 'components/layout/forms/Form';
import Input from 'components/layout/forms/Input';
import InputRow from 'components/layout/forms/InputRow';
import Image from 'components/layout/Image';

import StyledComponent from './styles';
import { Props } from './types';

interface HandleValue {
    [key: string]: string | number | any;
}

interface FormState {
    name: string;
    vatId: string;
    city: string;
    postCode: string;
    street: string;
    homeNo: string;
    flatNo: string;
    fullName: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    agrees: UserAgree[];
    gCredential: string;
}

interface Errors extends FormState {};

const ContentSectionsCompanyRegisterForm: FunctionComponent<Props> = ({ translations, onSuccess }) => {
    const [formValues, setFormValues] = useState<FormState>(null);
    const [errorMessage, setErrorMessage] = useState<Errors>(null);
    const [isPending, setIsPending] = useState<boolean>(false);

    const bindGoogle = Boolean(formValues?.gCredential);

    const submitForm = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsPending(true);

        await registerRequest({
            ...formValues,
            agrees: formValues?.agrees && Object.values(formValues.agrees),
        })
            .then(() => onSuccess && onSuccess())
            .catch((errors) => setErrorMessage(getErrorsFromRequest(errors)));

        setIsPending(false);
    };

    const onChange = (value: HandleValue) => setFormValues((prevState: FormState) => ({ ...prevState, ...value }));

    return (
        <StyledComponent className="content-section-company-register">
            <Form
                onSubmit={submitForm}
                className="register-form"
            >
                <h2 className="title">{translations?.titleCompany}</h2>
                <Input
                    name="name"
                    label={translations?.inputCompanyName}
                    placeholder={translations?.inputCompanyName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ name: e.target.value })}
                    value={formValues?.name}
                    error={errorMessage?.name}
                    disabled={isPending}
                />
                <Input
                    className="vat-id-input"
                    name="vatId"
                    label={translations?.inputVatId}
                    placeholder={translations?.inputVatId}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ vatId: e.target.value })}
                    value={formValues?.vatId}
                    error={errorMessage?.vatId}
                    disabled={isPending}
                />
                <InputRow>
                    <Input
                        name="city"
                        label={translations?.inputCity}
                        placeholder={translations?.inputCity}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ city: e.target.value })}
                        value={formValues?.city}
                        error={errorMessage?.city}
                        disabled={isPending}
                    />
                    <Input
                        name="postCode"
                        label={translations?.inputPostCode}
                        placeholder={translations?.inputPostCode}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ postCode: e.target.value })}
                        value={formValues?.postCode}
                        error={errorMessage?.postCode}
                        disabled={isPending}
                    />
                </InputRow>
                <Input
                    name="street"
                    label={translations?.inputStreet}
                    placeholder={translations?.inputStreet}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ street: e.target.value })}
                    value={formValues?.street}
                    error={errorMessage?.street}
                    disabled={isPending}
                />
                <InputRow>
                    <Input
                        name="homeNo"
                        label={translations?.inputHomeNo}
                        placeholder={translations?.inputHomeNo}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ homeNo: e.target.value })}
                        value={formValues?.homeNo}
                        error={errorMessage?.homeNo}
                        disabled={isPending}
                    />
                    <Input
                        name="flatNo"
                        label={translations?.inputFlatNo}
                        placeholder={translations?.inputFlatNo}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ flatNo: e.target.value })}
                        value={formValues?.flatNo}
                        error={errorMessage?.flatNo}
                        disabled={isPending}
                    />
                </InputRow>
                {!bindGoogle && (
                    <ButtonGoogle
                        headline={translations?.googleHeadline}
                        onSuccess={(gCredential, googleUserDetails) => {
                            setFormValues((prevState: FormState) => ({
                                ...prevState,
                                ...googleUserDetails,
                                gCredential,
                            }));
                        }}
                        onError={() => alert(translations?.googleError)}
                    />
                )}
                {bindGoogle && (
                    <div className="google-binded">
                        <p className="headline">{translations?.googleHeadlineBind}</p>
                        <div className="details">
                            <Image
                                className="icon"
                                src='/icons/google.svg'
                                layout='fixed'
                                objectFit='scale-down'
                                objectPosition="center"
                                width={28}
                                height={28}
                            />
                            <div className="details-user">
                                <p className="details-user-name">{formValues?.fullName}</p>
                                <p className="details-user-email">{formValues?.email}</p>
                            </div>
                        </div>
                    </div>
                )}
                <h2 className="title title-margin">{bindGoogle ? translations?.titlePersonGoogle : translations?.titlePerson}</h2>
                <InputRow>
                    <Input
                        name="firstName"
                        label={translations?.inputFirstName}
                        placeholder={translations?.inputFirstName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ firstName: e.target.value })}
                        value={formValues?.firstName}
                        error={errorMessage?.firstName}
                        disabled={isPending}
                    />
                    <Input
                        name="lastName"
                        label={translations?.inputLastName}
                        placeholder={translations?.inputLastName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ lastName: e.target.value })}
                        value={formValues?.lastName}
                        error={errorMessage?.lastName}
                        disabled={isPending}
                    />
                </InputRow>
                <Input
                    name="email"
                    label={translations?.inputEmail}
                    placeholder={translations?.inputEmail}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ email: e.target.value })}
                    value={formValues?.email}
                    error={errorMessage?.email}
                    disabled={bindGoogle}
                />
                <Input
                    name="phone"
                    label={translations?.inputPhone}
                    placeholder={translations?.inputPhone}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ phone: e.target.value })}
                    value={formValues?.phone}
                    error={errorMessage?.phone}
                    disabled={isPending}
                />
                {
                    typesOptions.map((option) => {
                        if (!option?.type) return;

                        return (
                            <Checkbox
                                key={option.type}
                                style={InputStyles.Required}
                                name={option.type}
                                label={option.content}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({
                                    agrees: {
                                        ...formValues?.agrees,
                                        [option.type]: {
                                            ...option,
                                            value: e.target.checked,
                                        },
                                    },
                                })}
                                value={formValues?.agrees ? formValues?.agrees[option.type]?.value : false}
                                error={errorMessage?.agrees as unknown as string}
                                disabled={isPending}
                            />
                        );
                    })
                }
                <div className="disclaimer">
                    Administratorem Twoich danych osobowych jest FIZJO AI sp. z o.o. z siedzibą w Lublinie przy ul. Gospodarczej 26, 20-213 Lublin.
                    Twoje dane osobowe przetwarzane będą w szczególności w celu wykonania umowy o świadczenie usług drogą elektroniczną, której to stajesz się stroną.
                    Gwarantujemy spełnienie Twoich praw wynikających z ogólnego rozporządzenia o ochronie danych. Zasady ochrony i przetwarzania Twoich danych osobowych znajdziesz tutaj:
                    <a
                        href="https://fizjoai.pl/polityka-prywatnosci"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Polityka Prywatności
                    </a>.

                </div>
                <Button
                    loading={isPending}
                    className="submit-button"
                    type={ButtonTypes.Submit}
                >
                    {translations?.submitButton}
                </Button>
            </Form>
        </StyledComponent>
    );
};

export default ContentSectionsCompanyRegisterForm;
